import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Button
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Payment as PaymentIcon
} from '@mui/icons-material';
import { getFirestore, doc, updateDoc, query, collection, where, getDocs } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../services/firebase/config';

/**
 * Page de retour après l'onboarding Stripe Connect réussi
 * Route: /activation-paiement-success
 */
const ActivationPaiementSuccess: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<'success' | 'error' | null>(null);
  const [message, setMessage] = useState('');

  useEffect(() => {
    const handleReturn = async () => {
      try {
        // Récupérer les paramètres de l'URL
        const accountId = searchParams.get('account');
        const onboardingComplete = searchParams.get('onboarding_complete');

        if (!accountId) {
          setStatus('error');
          setMessage('Paramètres manquants. Veuillez réessayer depuis vos paramètres.');
          setLoading(false);
          return;
        }

        // Vérifier le statut du compte Stripe
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
        const accountResponse = await fetch(`${apiUrl}/api/stripe/connected-account/${accountId}`);
        const accountData = await accountResponse.json();

        if (!accountData.success || !accountData.account) {
          setStatus('error');
          setMessage('Impossible de vérifier le statut de votre compte Stripe.');
          setLoading(false);
          return;
        }

        const account = accountData.account;
        const isOnboardingComplete = account.charges_enabled && account.payouts_enabled;

        // Trouver l'utilisateur avec ce connectedAccountId
        let userId = user?.uid;
        
        if (!userId) {
          // Si pas d'utilisateur connecté, chercher par connectedAccountId
          const usersQuery = query(
            collection(db, 'users'),
            where('stripe.connectedAccountId', '==', accountId)
          );
          const usersSnapshot = await getDocs(usersQuery);
          
          if (!usersSnapshot.empty) {
            userId = usersSnapshot.docs[0].id;
          }
        }

        if (!userId) {
          setStatus('error');
          setMessage('Utilisateur non trouvé. Veuillez vous connecter.');
          setLoading(false);
          return;
        }

        // Mettre à jour le statut en base de données
        await updateDoc(doc(db, 'users', userId), {
          'stripe.connectedAccountId': accountId,
          'stripe.chargesEnabled': account.charges_enabled || false,
          'stripe.payoutsEnabled': account.payouts_enabled || false,
          'stripe.detailsSubmitted': account.details_submitted || false,
          'stripe.onboardingCompleted': isOnboardingComplete
        });

        if (isOnboardingComplete || onboardingComplete === 'true') {
          setStatus('success');
          setMessage('Félicitations ! Vos paiements sont maintenant activés. Vous pouvez recevoir l\'argent de vos locations de robes.');
        } else {
          setStatus('error');
          setMessage('L\'activation de vos paiements n\'a pas été complétée. Veuillez compléter toutes les étapes de l\'onboarding Stripe.');
        }
      } catch (error) {
        console.error('Erreur lors du retour Stripe:', error);
        setStatus('error');
        setMessage('Une erreur est survenue. Veuillez réessayer.');
      } finally {
        setLoading(false);
      }
    };

    handleReturn();
  }, [searchParams, user]);

  const handleGoToSettings = () => {
    navigate('/dashboard/settings');
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '100vh',
        p: 3
      }}>
        <CircularProgress size={60} sx={{ color: '#C9B6AB', mb: 2 }} />
        <Typography variant="h6" sx={{ color: '#C9B6AB' }}>
          Vérification de votre compte...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      justifyContent: 'center', 
      minHeight: '100vh',
      p: 3
    }}>
      <Card sx={{ maxWidth: 500, width: '100%' }}>
        <CardContent sx={{ textAlign: 'center', p: 4 }}>
          {status === 'success' ? (
            <>
              <CheckCircleIcon sx={{ fontSize: 80, color: '#4caf50', mb: 2 }} />
              <Typography variant="h4" sx={{ color: '#4caf50', mb: 2 }}>
                Paiements activés !
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                {message}
              </Typography>
              <Alert severity="success" sx={{ mb: 3 }}>
                Vous pouvez maintenant recevoir automatiquement l'argent de vos locations de robes.
              </Alert>
            </>
          ) : (
            <>
              <ErrorIcon sx={{ fontSize: 80, color: '#f44336', mb: 2 }} />
              <Typography variant="h4" sx={{ color: '#f44336', mb: 2 }}>
                Activation incomplète
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                {message}
              </Typography>
              <Alert severity="warning" sx={{ mb: 3 }}>
                Vous pouvez réessayer l'activation depuis vos paramètres.
              </Alert>
            </>
          )}
          
          <Button
            variant="contained"
            onClick={handleGoToSettings}
            startIcon={<PaymentIcon />}
            sx={{
              backgroundColor: '#C9B6AB',
              '&:hover': {
                backgroundColor: '#C1A08E',
              },
            }}
          >
            Retour aux paramètres
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ActivationPaiementSuccess;



